import {
  VibeSdk,
  completePopupCallback,
  type CalendarEvent,
  type CalendarStatus,
  type CalendarSummary,
  type VibeProfile,
  type VibeScope,
} from "../sdk";

declare const __VIBE_NODE_ORIGIN__: string;

const scopes: VibeScope[] = ["profile:read", "calendar.read", "calendar.sync"];
const root = document.querySelector<HTMLElement>("#app")!;
const sdk = new VibeSdk({ nodeOrigin: __VIBE_NODE_ORIGIN__ });

if (completePopupCallback()) {
  root.innerHTML = `<section class="hero"><p>Returning to Vibe Calendar…</p></section>`;
} else {
  void start();
}

async function start(): Promise<void> {
  root.innerHTML = `<section class="hero"><p>Opening your calendar…</p></section>`;
  try {
    await sdk.completeRedirectCallback();
    if (!scopes.every((scope) => sdk.hasToken(scope)) && !await sdk.resume(scopes)) {
      await sdk.connectManyWithRedirect(scopes);
    }
    await renderCalendar();
  } catch (error) {
    renderWelcome(messageOf(error));
  }
}

function renderWelcome(error?: string): void {
  root.innerHTML = `<section class="hero"><div class="eyebrow">Vibe Calendar</div><h1>Sign in to your calendar.</h1><p>Use your Vibe account to continue. Your password stays with Vibe Cloud; Calendar receives only the calendars and events you explicitly chose to copy.</p><div class="button-row"><button id="sign-in" type="button">Try signing in again</button></div>${error ? `<p class="error">${h(error)}</p>` : ""}<p class="sign-in-note">Vibe securely handles your identity in this tab.</p></section>`;
  document.querySelector<HTMLButtonElement>("#sign-in")!.addEventListener("click", () => void sdk.connectManyWithRedirect(scopes));
}

async function renderCalendar(): Promise<void> {
  const [profile, status, calendars, events] = await Promise.all([
    sdk.profile(),
    sdk.calendar.status(),
    sdk.calendar.calendars(),
    sdk.calendar.events({ limit: 500 }),
  ]);
  render(profile, status, calendars, events);
}

function render(profile: VibeProfile, status: CalendarStatus, calendars: CalendarSummary[], events: CalendarEvent[]): void {
  const connection = !status.configured
    ? `<div class="empty"><strong>Google Calendar is not available on this node yet.</strong><p>The node operator must configure the read-only Google connection first.</p></div>`
    : !status.connected
      ? `<div class="empty"><strong>No Google Calendar connection.</strong><p>Connect it in Vibe Cloud, then choose which calendars to copy.</p><a class="button" href="${h(sdk.nodeOrigin)}/services" target="_blank" rel="noopener noreferrer">Open Vibe Cloud services</a></div>`
      : calendars.length === 0
        ? `<div class="empty"><strong>No calendars selected.</strong><p>Choose at least one calendar in Vibe Cloud services. The app cannot change your Google connection.</p><a class="button" href="${h(sdk.nodeOrigin)}/services" target="_blank" rel="noopener noreferrer">Choose calendars</a></div>`
        : events.length === 0
          ? `<div class="empty"><strong>Your agenda is clear.</strong><p>No current event copies were returned for the selected calendars.</p></div>`
          : `<div class="events">${events.map(eventTemplate).join("")}</div>`;
  root.innerHTML = `<header class="topbar"><div class="brand"><span class="brand-mark" aria-hidden="true">V</span>Vibe Calendar</div><span class="profile">${h(profile.displayName)}</span></header><div class="dashboard"><aside class="panel"><div class="eyebrow">Calendars</div><div class="calendar-list">${calendars.map((calendar) => `<div class="calendar-row"><span class="swatch"></span><div><strong>${h(calendar.summary)}</strong><span>${h(calendar.timeZone ?? "Timezone not reported")}</span></div></div>`).join("") || `<p>None selected</p>`}</div><p class="notice">Read-only copy · managed by your Vibe Cloud node</p></aside><section class="panel"><div class="agenda-head"><div><div class="eyebrow">Agenda</div><h2>Upcoming events</h2><div class="sync">${status.lastSyncAt ? `Last refreshed ${h(new Date(status.lastSyncAt).toLocaleString())}` : "Not refreshed yet"}</div></div>${status.connected ? `<button id="refresh" class="secondary" type="button">Refresh</button>` : ""}</div>${connection}</section></div>`;
  document.querySelector<HTMLButtonElement>("#refresh")?.addEventListener("click", async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    button.textContent = "Refreshing…";
    try {
      await sdk.calendar.refresh();
      await renderCalendar();
    } catch (error) {
      button.disabled = false;
      button.textContent = "Try again";
      root.querySelector<HTMLElement>(".sync")!.textContent = messageOf(error);
    }
  });
}

function eventTemplate(event: CalendarEvent): string {
  const start = event.allDay
    ? event.startDate ? new Date(`${event.startDate}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "All day"
    : event.startAt ? new Date(event.startAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Time unavailable";
  return `<article class="event"><time>${h(start)}</time><div><strong>${h(event.summary ?? "Busy")}</strong>${event.location ? `<p>${h(event.location)}</p>` : ""}</div></article>`;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : "Vibe Calendar could not load.";
}

function h(value: string): string {
  return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!);
}
